import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Card, Field, TextInput, SelectInput, ErrorBox, btnPrimary, btnSecondary, StatusBadge, fmtDate } from "@/components/common/Form";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Pager } from "./AdminUsers";

function PermPicker({ all, value, onChange, prefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((p) => p !== k) : [...value, k]);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 text-xs" data-testid={`${prefix}-perms`}>
      {all.map((p) => (
        <label key={p.key} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 cursor-pointer ${value.includes(p.key) ? "border-primary bg-accent/50" : "border-border"}`}>
          <input type="checkbox" data-testid={`${prefix}-perm-${p.key}`} checked={value.includes(p.key)} onChange={() => toggle(p.key)} /> <span><b>{p.key}</b><span className="text-stone-500"> · {p.label}</span></span>
        </label>
      ))}
    </div>
  );
}

export function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => Promise.all([api.get("/admin/staff"), api.get("/admin/roles")]).then(([s, r]) => { setStaff(s.data); setMeta(r.data); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  if (!staff || !meta) return <LoadingState />;
  const create = async (e) => {
    e.preventDefault(); setError("");
    try { await api.post("/admin/staff", form); toast.success("Staff account created"); setForm(null); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const savePerms = async () => {
    try { await api.put(`/admin/staff/${editing.id}/permissions`, { permissions: editing.extra_permissions }); toast.success("Permissions updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setStatus = async (u, status) => {
    try { await api.post(`/admin/users/${u.id}/status`, { status, reason: "Staff access change" }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Staff" subtitle="Internal team members with permission-based access." testId="admin-staff"
        action={!form && <button data-testid="staff-add" onClick={() => setForm({ first_name: "", last_name: "", email: "", password: "", phone: "", permissions: [] })} className={btnPrimary}>Add staff</button>}>
        {form && (
          <form onSubmit={create} className="rounded-xl bg-muted/60 p-4 mb-5 grid sm:grid-cols-2 gap-3" data-testid="staff-form">
            <div className="sm:col-span-2"><ErrorBox message={error} testId="staff-error" /></div>
            <Field label="First name *"><TextInput testId="staff-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Last name"><TextInput testId="staff-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Email *"><TextInput testId="staff-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Temporary password *" hint="8+ chars, upper, lower, number"><TextInput testId="staff-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <div className="sm:col-span-2"><div className="text-sm text-stone-600 mb-1.5">Additional permissions (beyond staff defaults)</div><PermPicker all={meta.permissions} value={form.permissions} onChange={(permissions) => setForm({ ...form, permissions })} prefix="staff-new" /></div>
            <div className="sm:col-span-2 flex gap-2"><button data-testid="staff-create" className={btnPrimary}>Create staff</button><button type="button" onClick={() => setForm(null)} className={btnSecondary}>Cancel</button></div>
          </form>
        )}
        {staff.length === 0 ? <EmptyState title="No staff yet" /> : (
          <ul className="divide-y divide-border text-sm" data-testid="staff-list">
            {staff.map((u) => (
              <li key={u.id} data-testid={`staff-row-${u.id}`} className="py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[180px]"><div className="font-medium text-stone-900">{u.name} <span className="text-xs text-stone-400 capitalize">· {u.role}</span></div><div className="text-xs text-stone-500">{u.email} · last login {fmtDate(u.last_login_at)}</div></div>
                  <StatusBadge value={u.status} />
                  {u.role === "staff" && <>
                    <button onClick={() => setEditing(editing?.id === u.id ? null : { id: u.id, extra_permissions: u.extra_permissions })} data-testid={`staff-perms-${u.id}`} className="text-xs font-semibold text-secondary">Permissions ({u.permissions?.length || 0})</button>
                    {u.status === "ACTIVE" ? <button onClick={() => setStatus(u, "INACTIVE")} data-testid={`staff-deactivate-${u.id}`} className="text-xs font-semibold text-red-600">Deactivate</button> : <button onClick={() => setStatus(u, "ACTIVE")} data-testid={`staff-activate-${u.id}`} className="text-xs font-semibold text-emerald-700">Activate</button>}
                  </>}
                </div>
                {editing?.id === u.id && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-stone-500">Staff defaults: {meta.roles.find((r) => r.key === "staff")?.permissions.join(", ") || "none"}</p>
                    <PermPicker all={meta.permissions} value={editing.extra_permissions} onChange={(extra_permissions) => setEditing({ ...editing, extra_permissions })} prefix={`staff-${u.id}`} />
                    <div className="flex gap-2"><button onClick={savePerms} data-testid={`staff-perms-save-${u.id}`} className={btnPrimary}>Save permissions</button><button onClick={() => setEditing(null)} className={btnSecondary}>Cancel</button></div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function AdminRoles() {
  const [meta, setMeta] = useState(null);
  const [staffPerms, setStaffPerms] = useState(null);
  const load = useCallback(() => api.get("/admin/roles").then((r) => { setMeta(r.data); setStaffPerms(r.data.roles.find((x) => x.key === "staff").permissions); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  if (!meta) return <LoadingState />;
  const save = async () => {
    try { await api.put("/admin/roles/staff/permissions", { permissions: staffPerms }); toast.success("Staff role permissions updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Roles" subtitle="System roles and their effective permissions." testId="admin-roles">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {meta.roles.map((r) => (
            <div key={r.key} data-testid={`role-card-${r.key}`} className="rounded-xl border border-border p-4 text-sm">
              <div className="flex items-center justify-between"><span className="font-semibold text-stone-900">{r.name}</span><span className="text-xs text-stone-500">{r.users} users</span></div>
              <p className="text-xs text-stone-500 mt-1">{r.key === "admin" ? "All permissions" : r.permissions.length ? r.permissions.join(", ") : "No admin permissions (marketplace user)"}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Staff role base permissions" subtitle="Every staff member receives these; extra permissions are assigned per person." testId="admin-role-staff-perms">
        <PermPicker all={meta.permissions} value={staffPerms} onChange={setStaffPerms} prefix="role-staff" />
        <button onClick={save} data-testid="role-staff-save" className={`${btnPrimary} mt-4`}>Save staff permissions</button>
      </Card>
    </div>
  );
}

export function AdminAudit() {
  const [filters, setFilters] = useState({ action: "", page: 1 });
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v !== ""));
    api.get(`/admin/audit-logs?${p}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card title="Audit logs" subtitle="Immutable record of security and account actions." testId="admin-audit"
      action={data && <SelectInput testId="audit-action-filter" value={filters.action} onChange={(e) => setFilters({ action: e.target.value, page: 1 })} options={[["", "All actions"], ...data.actions.map((a) => [a, a])]} />}>
      {!data ? <LoadingState /> : data.items.length === 0 ? <EmptyState title="No audit entries" /> : (
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full text-sm min-w-[760px]" data-testid="audit-table">
            <thead><tr className="text-left text-xs text-stone-500 border-b border-border"><th className="py-2 px-3">Time</th><th className="px-3">Actor</th><th className="px-3">Action</th><th className="px-3">Resource</th><th className="px-3">Details</th><th className="px-3">IP</th></tr></thead>
            <tbody>{data.items.map((l) => (
              <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-border/60 align-top">
                <td className="py-2 px-3 text-xs text-stone-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                <td className="px-3 text-xs">{l.actor_email || l.actor_user_id || "system"}</td>
                <td className="px-3 font-semibold text-xs text-stone-800">{l.action}</td>
                <td className="px-3 text-xs">{l.target_type}<br /><span className="text-stone-400">{l.target_id}</span></td>
                <td className="px-3 text-xs text-stone-600 max-w-xs break-words">{Object.entries(l.metadata || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") || "—"}</td>
                <td className="px-3 text-xs text-stone-400">{l.ip_reference || "—"}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      <Pager data={data} onPage={(page) => setFilters({ ...filters, page })} />
    </Card>
  );
}
